import React from 'react';
import { Link } from 'react-router-dom';
import { Car, ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900 hover:text-red-600 transition-colors">
            <Car className="w-8 h-8 text-red-600" />
            <span className="text-xl font-bold">AutoFlow</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="bg-white rounded-xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Términos y Condiciones</h1>
          <p className="text-slate-500 mb-8 pb-8 border-b">
            <strong>AutoFlow</strong> | Última actualización: abril 2025
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Aceptación</h2>
              <p className="text-slate-600 leading-relaxed">
                Al usar los servicios de AutoFlow, ya sea como agencia suscripta o como usuario final de un bot de WhatsApp, aceptás estos términos en su totalidad.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Descripción del servicio</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                AutoFlow es una plataforma de gestión comercial para agencias de automóviles que incluye:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Panel de administración web para agencias</li>
                <li>Asistentes de WhatsApp con inteligencia artificial</li>
                <li>Gestión de leads y seguimiento comercial</li>
                <li>Integración con WhatsApp Business API oficial de Meta</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Uso de los asistentes de WhatsApp</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Los bots de WhatsApp de AutoFlow son asistentes comerciales automatizados. Al interactuar con ellos:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Entendés que estás hablando con un sistema automatizado de la agencia</li>
                <li>La información provista por el bot es orientativa y no constituye una oferta comercial vinculante</li>
                <li>Los precios, disponibilidad y condiciones finales deben confirmarse directamente con la agencia</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Responsabilidad de las agencias</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Cada agencia suscripta a AutoFlow es responsable de:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>La veracidad de la información de vehículos cargada en el sistema</li>
                <li>El uso adecuado de los datos de sus clientes</li>
                <li>Cumplir con la legislación aplicable en materia de protección de datos</li>
                <li>Gestionar los consentimientos necesarios de sus usuarios</li>
              </ul>
              <p className="text-slate-700 font-medium mt-4 p-3 bg-slate-50 rounded-lg">
                AutoFlow actúa como proveedor tecnológico y no es responsable por el contenido comercial de cada agencia.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Prohibiciones</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Queda prohibido usar AutoFlow para:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Enviar mensajes masivos no solicitados (spam)</li>
                <li>Recopilar datos sin consentimiento del usuario</li>
                <li>Cualquier actividad ilegal o que viole las políticas de Meta/WhatsApp</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Disponibilidad del servicio</h2>
              <p className="text-slate-600 leading-relaxed">
                AutoFlow no garantiza disponibilidad ininterrumpida del servicio. Nos reservamos el derecho de realizar mantenimientos programados notificando con anticipación razonable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Propiedad intelectual</h2>
              <p className="text-slate-600 leading-relaxed">
                El software, diseño y contenidos de AutoFlow son propiedad exclusiva de sus creadores. Las agencias reciben una licencia de uso no transferible mientras dure su suscripción.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Limitación de responsabilidad</h2>
              <p className="text-slate-600 leading-relaxed">
                AutoFlow no será responsable por daños indirectos, pérdida de datos o lucro cesante derivados del uso o imposibilidad de uso del servicio.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Modificaciones</h2>
              <p className="text-slate-600 leading-relaxed">
                Podemos modificar estos términos en cualquier momento. El uso continuado del servicio implica aceptación de los términos actualizados.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Ley aplicable</h2>
              <p className="text-slate-600 leading-relaxed">
                Estos términos se rigen por las leyes de la República Argentina. Cualquier disputa se resolverá ante los tribunales competentes de la Ciudad de Mendoza.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Contacto</h2>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-700 font-medium">AutoFlow</p>
                <p className="text-slate-600">autoflowapp.net</p>
                <p className="text-slate-600">
                  Email:{' '}
                  <a href="mailto:legal@autoflowapp.net" className="text-red-600 hover:underline">
                    legal@autoflowapp.net
                  </a>
                </p>
              </div>
            </section>
          </div>
        </article>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="hover:text-red-600 transition-colors font-medium text-slate-700">
            Términos y Condiciones
          </Link>
          <span className="mx-2">•</span>
          <Link to="/privacy" className="hover:text-red-600 transition-colors">
            Política de Privacidad
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Car className="w-5 h-5 text-red-500" />
            <span className="font-semibold">AutoFlow</span>
          </div>
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} AutoFlow. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
